import { useId, type JSX, type ReactNode } from 'react';
import { useTranslation } from '../i18n';
import { Icon, type IconName } from './Icon';

/**
 * Раздел панели, который сворачивается в одну строку.
 *
 * Заведён для панели узла: разделы VNC, SSH, камеры и регистратора
 * разворачивались независимо, и открытые разом они давали панель
 * в два-три экрана прокрутки — а на телефоне, где панель приходит шторкой,
 * и того длиннее. Разделы там теперь взаимно исключающие (см. `NodePanel`),
 * но само знание «раскрыт ли я» этому компоненту не принадлежит: он
 * управляемый, и кто с кем спорит за место, решает панель.
 *
 * Свёрнутый раздел — ровно одна строка: заголовок и кнопка действия.
 * Итоги зондирования и прочие сводки уходят внутрь: строка, которая
 * висит под свёрнутым заголовком, удваивает высоту всего, что свёрнуто,
 * и экономия прокрутки пропадает.
 *
 * Щелчок принимает не один шеврон, а заголовок вместе со всем свободным
 * местом строки (`flex-1`). Обернуть в кнопку строку целиком нельзя:
 * в ней стоят «Подключиться» и «На стену», а кнопка внутри
 * кнопки — невалидная разметка.
 */
export function CollapsibleSection({
  title,
  icon,
  actions,
  open,
  onToggle,
  children,
}: {
  title: string;
  /**
   * Глиф раздела — крайним слева, перед шевроном.
   *
   * Свёрнутая панель это десяток одинаковых строк, и нужную в них ищут
   * по слову. Глиф отвечает на тот же вопрос раньше, чем слово прочитано,
   * и держит левый край на месте: шеврон при раскрытии поворачивается,
   * а глиф остаётся якорем.
   */
  icon?: IconName;
  /** Кнопки справа в строке заголовка. Видны и в свёрнутом виде. */
  actions?: ReactNode;
  /**
   * `null` — сворачивать нечего: шеврона нет, содержимое видно всегда.
   *
   * Так показывается раздел ещё не заведённого устройства: внутри у него
   * одна форма создания, и спрятать её за шеврон значит спрятать
   * единственный способ завести камеру.
   */
  open: boolean | null;
  onToggle: () => void;
  children: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  const bodyId = useId();

  const head = (
    <>
      {icon ? (
        <span className="flex text-dim group-hover:text-fg">
          <Icon name={icon} />
        </span>
      ) : null}
      {open === null ? (
        /* Пустое место вместо шеврона: без него заголовок несворачиваемого
           раздела встал бы левее соседних, и левый край панели поехал бы. */
        <span className="size-4 flex-none" />
      ) : (
        <span
          className={`flex text-dim group-hover:text-fg transition-transform ${open ? 'rotate-90' : ''}`}
        >
          <Icon name="chevronRight" />
        </span>
      )}
      {title}
    </>
  );

  return (
    <section className="flex flex-col gap-2.5 border-b border-line px-3.5 py-2.5">
      {/* `flex-wrap` — кнопки действий с непереносимыми подписями («Видео
          архив» и «На стену») в узкую панель в одну строку с заголовком
          не помещаются. */}
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="min-w-0 flex-1 text-sm font-semibold">
          {open === null ? (
            <span className="flex w-full items-center gap-1.5">{head}</span>
          ) : (
            <button
              type="button"
              className="group flex w-full items-center gap-1.5 text-left"
              aria-expanded={open}
              aria-controls={bodyId}
              title={open ? t('common.action.collapse') : t('common.action.expand')}
              onClick={onToggle}
            >
              {head}
            </button>
          )}
        </h4>
        {actions}
      </div>

      {open === false ? null : (
        <div id={bodyId} className="flex flex-col gap-2.5">
          {children}
        </div>
      )}
    </section>
  );
}
